import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { StatesProvincesEntity } from './states-provinces.entity';
import { SupportedPaymentMethodTypeEntity } from './supported-payment-method-type.entity';

/**
 * PD-15587 — this table and `supported_payment_method_type_state` both record
 * "this payment method type, in this state" and are now written from a SINGLE
 * list in the settings editor: every allowed state gets a row here carrying its
 * rate, and a state that should not be surcharged carries a rate of 0 rather
 * than being absent.
 *
 * Collapsing them into one table is deferred until at least South Pointe is on
 * SM2, because doing it means migrating tenant data and touching every reader.
 * Nothing is inconsistent in the meantime — the editor is the only writer.
 *
 * The migration to watch when that happens: a state currently allowed but with
 * NO row here charges nothing, so it must become an explicit 0, not an
 * inherited rate.
 */
@Index('IDX_fk_state_province__id_fee', ['fkStateProvinceId'], {})
@Index(
	'IDX_fk_supported_payment_method_type__id_fee',
	['fkSupportedPaymentMethodTypeId'],
	{},
)
@Index(
	'UK_supportedPaymentMethodType_feeState',
	['fkSupportedPaymentMethodTypeId', 'fkStateProvinceId'],
	{ unique: true },
)
@Entity('supported_payment_method_type_fee_state')
export class SupportedPaymentMethodTypeFeeStateEntity extends BaseEntity {
	@Column('int', {
		name: 'fk_state_province__id',
		nullable: true,
		unsigned: true,
	})
	public fkStateProvinceId!: number | null;

	@Column('int', {
		name: 'fk_supported_payment_method_type__id',
		unsigned: true,
	})
	public fkSupportedPaymentMethodTypeId!: number;

	/**
	 * PD-15583 — the fee UNIT charged in this state, overriding the payment
	 * method type's own unit.
	 *
	 * NULL means "inherit the type's unit", which is what every row written
	 * before this column existed carries — so an untouched configuration keeps
	 * behaving exactly as it did.
	 *
	 * Overridable independently of the amount: a state may cap the rate
	 * (3% -> 2%) without changing the unit, or switch to a flat charge
	 * entirely. `resolveStateProcessingFee` owns the fallback so the row is
	 * never read directly.
	 */
	@Column('enum', {
		name: 'processingFeeType',
		enum: ['%', '$'],
		nullable: true,
	})
	public processingFeeType!: '%' | '$' | null;

	/**
	 * PD-15583 — the fee AMOUNT charged in this state, overriding the payment
	 * method type's own amount. NULL inherits the type's amount.
	 *
	 * Zero is a meaningful value and is NOT the same as NULL: it says this
	 * state is listed but charges nothing, which a tenant may want in order to
	 * record a deliberate exemption rather than deleting the row. Every read
	 * therefore tests `!= null` rather than truthiness.
	 *
	 * A state override makes sense even when the type's own amount is zero
	 * (no fee by default, a surcharge in one state), so nothing keys the
	 * override off the type's amount being positive.
	 */
	@Column('decimal', {
		name: 'processingFee',
		precision: 10,
		scale: 2,
		nullable: true,
	})
	public processingFee!: number | null;

	@ManyToOne(
		() => SupportedPaymentMethodTypeEntity,
		(supportedPaymentMethodType) =>
			supportedPaymentMethodType.supportedPaymentMethodTypeFeeStates,
		{ onDelete: 'CASCADE', onUpdate: 'NO ACTION' },
	)
	@JoinColumn([
		{
			name: 'fk_supported_payment_method_type__id',
			referencedColumnName: 'id',
		},
	])
	public fkSupportedPaymentMethodType!: SupportedPaymentMethodTypeEntity;

	@ManyToOne(
		() => StatesProvincesEntity,
		(statesProvinces) => statesProvinces.supportedPaymentMethodTypeFeeStates,
		{ onDelete: 'NO ACTION', onUpdate: 'NO ACTION' },
	)
	@JoinColumn([{ name: 'fk_state_province__id', referencedColumnName: 'id' }])
	public fkStateProvince!: StatesProvincesEntity;
}
